import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import Box from '@mui/material/Box';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import React, { useCallback } from 'react';

import { NUMERIC_CELL_SX, STICKY_LABEL_CELL_SX } from './ServiceActivityMatrix.styles';
import { ActivityMatrixGroup } from '../../../@types/service-activity.type';
import { COLORS } from '../../../configs/colors';
import { ACTIVITY_STATUS_COLUMNS } from '../../../constants/service-activity.constant';

export interface MatrixGroupRowProps {
  group: ActivityMatrixGroup;
  expanded: boolean;
  /** id of the tbody this row controls, for aria-controls. */
  bodyId: string;
  /** Single-member groups render without a toggle — nothing to expand into. */
  collapsible: boolean;
  canDrillDown: boolean;
  onToggle: (groupKey: string) => void;
  onSelect: (group: ActivityMatrixGroup) => void;
}

const GROUP_CELL_SX = {
  bgcolor: 'v4.tableEven',
  borderTop: `1px solid ${COLORS.SubtleGrey}`,
};

export const MatrixGroupRow: React.FC<MatrixGroupRowProps> = ({
  group,
  expanded,
  bodyId,
  collapsible,
  canDrillDown,
  onToggle,
  onSelect,
}) => {
  const handleToggle = useCallback(() => onToggle(group.key), [onToggle, group.key]);
  const handleSelect = useCallback(() => onSelect(group), [onSelect, group]);

  return (
    <TableRow>
      <TableCell
        component="th"
        scope="rowgroup"
        sx={{ ...STICKY_LABEL_CELL_SX, ...GROUP_CELL_SX, zIndex: 1 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', pl: '4px', pr: '8px' }}>
          {collapsible ? (
            <Box
              component="button"
              type="button"
              onClick={handleToggle}
              aria-expanded={expanded}
              aria-controls={bodyId}
              aria-label={`${expanded ? 'Collapse' : 'Expand'} ${group.label}`}
              sx={{
                display: 'flex',
                alignItems: 'center',
                border: 'none',
                bgcolor: 'transparent',
                cursor: 'pointer',
                p: '2px',
                borderRadius: 1,
                color: 'text.secondary',
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
              }}
            >
              {expanded ? (
                <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
              ) : (
                <KeyboardArrowRightIcon sx={{ fontSize: 16 }} />
              )}
            </Box>
          ) : (
            <Box aria-hidden sx={{ width: 20 }} />
          )}

          {canDrillDown ? (
            <Box
              component="button"
              type="button"
              onClick={handleSelect}
              aria-label={`${group.label}: ${group.total.toLocaleString()} open jobs. Open the filtered job list.`}
              sx={{
                border: 'none',
                bgcolor: 'transparent',
                font: 'inherit',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.02em',
                color: 'text.primary',
                cursor: 'pointer',
                p: 0,
                textAlign: 'left',
                '&:hover': { textDecoration: 'underline' },
                '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
              }}
            >
              {group.label}
            </Box>
          ) : (
            <Box component="span" sx={{ fontSize: '12px', fontWeight: 700 }}>
              {group.label}
            </Box>
          )}
        </Box>
      </TableCell>

      {ACTIVITY_STATUS_COLUMNS.map((column) => (
        <TableCell key={column.field} sx={{ ...NUMERIC_CELL_SX, ...GROUP_CELL_SX }}>
          <Box
            component="span"
            sx={{
              display: 'block',
              py: '5px',
              fontSize: '12px',
              fontWeight: 700,
              color: group.subtotals[column.field] === 0 ? COLORS.ActivityZeroText : 'text.primary',
            }}
          >
            {group.subtotals[column.field].toLocaleString()}
          </Box>
        </TableCell>
      ))}

      <TableCell sx={{ ...NUMERIC_CELL_SX, ...GROUP_CELL_SX }}>
        <Box
          component="span"
          sx={{ display: 'block', py: '5px', fontSize: '12px', fontWeight: 700 }}
        >
          {group.total.toLocaleString()}
        </Box>
      </TableCell>
    </TableRow>
  );
};
